import { apiClient, type MemberRole } from "@nabble/sdk";
import type { UserStatus } from "../model/userStatus";

export interface OrganizationMember {
  id: string;
  displayName: string;
  email: string;
  status: UserStatus;
  mfaEnrolled: boolean;
  /** Every role they hold here, base roles first. */
  roles: MemberRole[];
  joinedAt: string;
  self: boolean;
}

/** An organization as one of its administrators manages it (outside the Admin Center). */
export interface ManagedOrganization {
  id: string;
  name: string;
  members: OrganizationMember[];
}

/** A role the organization's administrators may or may not give (those reaching the whole suite they can't). */
export interface AssignableRole {
  id: string;
  name: string;
  description: string;
  system: boolean;
  assignable: boolean;
}

export function fetchAssignableRoles(id: string): Promise<AssignableRole[]> {
  return apiClient<AssignableRole[]>(`/api/organizations/${id}/roles`);
}

export function fetchOrganization(id: string): Promise<ManagedOrganization> {
  return apiClient<ManagedOrganization>(`/api/organizations/${id}`);
}

export function renameOrganization(id: string, name: string): Promise<void> {
  return apiClient<void>(`/api/organizations/${id}`, { method: "PUT", body: { name } });
}

/** Adds someone by email: they join at once if they have an account, otherwise they're invited. */
export function addMember(id: string, body: { email: string; displayName: string; roleIds: string[] }): Promise<void> {
  return apiClient<void>(`/api/organizations/${id}/members`, { method: "POST", body });
}

/** Replaces the roles a member holds in the organization. */
export function setMemberRoles(id: string, userId: string, roleIds: string[]): Promise<void> {
  return apiClient<void>(`/api/organizations/${id}/members/${userId}`, { method: "PUT", body: { roleIds } });
}

export function removeMember(id: string, userId: string): Promise<void> {
  return apiClient<void>(`/api/organizations/${id}/members/${userId}`, { method: "DELETE" });
}
